/**
 * Participation domain.
 *
 * Class-participation tracking — the hat-draw picker that captures whether a
 * learner contributed (yes), declined (no), or was passed over (pass).
 *
 * Half-domain bridge:
 *   - Events flow through `GenericEventService` like every other domain, so
 *     the unified timeline, stats engine, weekly summary, and rules engine
 *     all "just work" without bespoke code paths.
 *   - The picker UI stays bespoke (same way attendance has a generic event
 *     service and a bespoke register UI). `inputSurfaces: []` keeps the
 *     domain off the generic input renderers; the picker hosts itself on
 *     class detail.
 *
 * The events collection aliases the legacy `participationEvents` path so the
 * existing data stays in place — the one-shot migration script only adds
 * standard domain fields (schoolId, domainId, studentName, …) and renames
 * `outcome → status`, `dismiss → pass`.
 *
 * Per-(teacher, class) working memory (hat, deferral counters, forced-return
 * slot) stays in `participationHats` outside the domain engine. The domain
 * engine owns events; ephemeral picker state stays a bespoke collection,
 * same as `attendanceRegisters` lives next to `attendanceEvents`.
 *
 * Schema shape: `primary` only — there is no user-entered follow-up. The
 * picker writes round/zone/tier/state itself; those are not declared on the
 * config because nothing in the engine iterates them.
 */

import { assertDomainShape } from '../schemaAssert.js';
import { buildDomainCapabilities, buildDomainSurfaces } from '../centralModel.js';

export const PARTICIPATION_DOMAIN_ID = 'participation';

const PRIMARY = {
  id: 'status',
  label: 'Status',
  type: 'multiChoice',
  options: [
    { value: 'yes',  label: 'Yes',  color: 'success' },
    { value: 'no',   label: 'No',   color: 'error'   },
    { value: 'pass', label: 'Pass', color: 'warning' },
  ],
};

const TERMINOLOGY = {
  event:    { label: 'participation entry', plural: 'participation entries', description: 'A single hat-draw outcome for a learner' },
  category: { label: 'status',              plural: 'statuses',              description: 'Yes, No, or Pass' },
  type:     { label: 'participation type',  plural: 'participation types',   description: '' },
  action:   { label: 'follow-up',           plural: 'follow-ups',            description: 'What rules trigger after a participation entry' },
  rule:     { label: 'rule',                plural: 'rules',                 description: '' },
  queue:    { label: 'participation queue', plural: 'participation queues',  description: '' },
};

export const participationDomainConfig = assertDomainShape({
  name: 'participation',
  label: 'Participation',
  description: 'Class participation — who answered, who passed, who was picked',
  protected: false,
  active: true,
  sortOrder: 6,
  icon: null,
  color: 'info',
  defaultInputMethod: 'list',
  defaultView: 'list',
  // Picker is bespoke; keep this domain off the generic input renderers.
  inputSurfaces: [],
  collections: {
    events: 'participationEvents',
    rules: 'participationRules',
    types: 'participationTypes',
    actions: 'participationActions',
    queue: 'participationQueue',
  },
  eventsCollection: 'participationEvents',
  rulesCollection: 'participationRules',
  capabilities: buildDomainCapabilities({
    collective: {
      enabled: true,
      aggregates: ['count', 'breakdown', 'trend', 'baseline'],
      audiences: ['staff', 'parent-summary'],
    },
  }),
  surfaces: buildDomainSurfaces({
    quickIssue: false,
    rules: false,
    classDisplay: true,
    dashboard: true,
    reports: true,
  }),
  primary: PRIMARY,
  followUp: [],
  terminology: TERMINOLOGY,
  contextTags: {},
  scope: { grades: [] },
});

export default participationDomainConfig;
